import { OrganizationsApi, OrganizationUserDTO, OrganizationUsersApi } from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { get } from 'lodash';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage/index';
import { getErrorMessage } from '../../utils/common.utils';
import { getCurrentOrganizationId } from '../account/account.selectors';
import { FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import { fetchOrganizationUsersWithRolesRequested } from '../organization/organization.actions';
import { createOrganizationsApi, createOrganizationUserApi, runSagaWithAuth } from '../utils/api.utils';
import {
  deleteOrganizationUserFailed,
  deleteOrganizationUserSuccess,
  DELETE_ORGANIZATION_USER_API_REQUESTED,
  DELETE_ORGANIZATION_USER_STORE_KEY,
  grantOrganizationUserRolesFailed,
  grantOrganizationUserRolesSuccess,
  GRANT_ORGANIZATION_USER_ROLES_API_REQUESTED,
  OrgnizationUserActionTypes,
  revokeOrganizationUserRolesFailed,
  revokeOrganizationUserRolesSuccess,
  REVOKE_ORGANIZATION_USER_ROLES_API_REQUESTED,
} from './organization-user.actions';
import { GrantOrganizationUserRolesPayload, RevokeOrganizationUserRolesPayload } from './organization-user.types';

// ****************
// WORKERS
// ****************

export function* deleteOrganizationUser({ payload }: { type: string; payload: string }) {
  try {
    const organizationId: string = yield select(getCurrentOrganizationId);
    const organizationApi: OrganizationsApi = yield createOrganizationsApi();

    yield call(runSagaWithAuth(() => organizationApi.removeUser(organizationId, payload)));
    yield put<OrgnizationUserActionTypes>(deleteOrganizationUserSuccess(payload));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: DELETE_ORGANIZATION_USER_STORE_KEY,
        message: 'Success! You have deleted a team member.',
        state: FlashState.SUCCESS,
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: DELETE_ORGANIZATION_USER_STORE_KEY, message: error, state: FlashState.ERROR })
    );
    yield put<OrgnizationUserActionTypes>(deleteOrganizationUserFailed(get(e, 'response.data.message', e)));
  }
}

export function* grantOrganizationUserRoles({ payload }: { type: string; payload: GrantOrganizationUserRolesPayload }) {
  try {
    const organizationUsersApi: OrganizationUsersApi = yield createOrganizationUserApi();
    yield call(runSagaWithAuth(() => organizationUsersApi.grantRoles(payload.userId, payload.organizationId)));
    yield put<OrgnizationUserActionTypes>(grantOrganizationUserRolesSuccess());
    yield put(fetchOrganizationUsersWithRolesRequested(payload.organizationId));
  } catch (e) {
    yield put<OrgnizationUserActionTypes>(grantOrganizationUserRolesFailed(get(e, 'response.data.message', e)));
  }
}

export function* revokeOrganizationUserRoles({ payload }: { type: string; payload: RevokeOrganizationUserRolesPayload }) {
  try {
    const organizationUsersApi: OrganizationUsersApi = yield createOrganizationUserApi();
    const response: AxiosResponse<OrganizationUserDTO> = yield call(
      runSagaWithAuth(() => organizationUsersApi.revokeRoles(payload.userId, payload.organizationId))
    );
    const orgUserId = response.data.id;
    yield put<OrgnizationUserActionTypes>(revokeOrganizationUserRolesSuccess(orgUserId));
    yield put(fetchOrganizationUsersWithRolesRequested(payload.organizationId));
  } catch (e) {
    yield put<OrgnizationUserActionTypes>(revokeOrganizationUserRolesFailed(get(e, 'response.data.message', e)));
  }
}

// ****************
// WATCHERS
// ****************
export function* watchOrganizationUserSagas() {
  yield takeLatest(DELETE_ORGANIZATION_USER_API_REQUESTED, deleteOrganizationUser);
  yield takeLatest(GRANT_ORGANIZATION_USER_ROLES_API_REQUESTED, grantOrganizationUserRoles);
  yield takeLatest(REVOKE_ORGANIZATION_USER_ROLES_API_REQUESTED, revokeOrganizationUserRoles);
}
